import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Select } from '../UI';

const AIUsageChart = ({ data = [], startDate, endDate, pageOnly = false, hasLogs = false }) => {
    const [metric, setMetric] = useState('cost');
    const points = useMemo(() => {
        const buckets = new Map(data.map((point) => [point.date, point]));
        const result = [];
        let date = dayjs(startDate);
        const end = dayjs(endDate);
        // The logs UI permits a maximum 30-day interval.
        while (date.isValid() && !date.isAfter(end, 'day') && result.length < 32) {
            const key = date.format('YYYY-MM-DD');
            result.push(buckets.get(key) || { date: key, cost: 0, tokens: 0, requests: 0 });
            date = date.add(1, 'day');
        }
        return result;
    }, [data, startDate, endDate]);
    const formatValue = (value) => metric === 'cost'
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 6 })}`
        : Number(value).toLocaleString();
    const max = Math.max(...points.map((point) => Number(point[metric]) || 0), 0);
    const ceiling = max || 1;
    const left = 85;
    const top = 15;
    const height = 160;
    const width = 865;
    const step = width / Math.max(points.length, 1);
    const labelStep = Math.max(1, Math.ceil(points.length / 8));

    return (
        <section className="my-4 rounded-lg border border-gray-200 bg-white p-4" aria-label="Daily AI usage chart">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">Usage over time</h3>
                    <p className="text-xs text-gray-500">Daily totals · UTC{pageOnly ? ' · Current table page' : ''}</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span>Show</span>
                    <Select
                        size="sm"
                        value={metric}
                        onChange={(e) => setMetric(e.target.value)}
                        options={[
                            { value: 'cost', label: 'Cost' },
                            { value: 'tokens', label: 'Tokens' },
                            { value: 'requests', label: 'Requests' },
                        ]}
                        className="w-32"
                    />
                </div>
            </div>
            <div className="overflow-x-auto">
                <svg viewBox="0 0 970 215" className="w-full min-w-[520px]" role="img" aria-label={`Daily AI ${metric} from ${startDate} to ${endDate}`}>
                    {[0, 1, 2, 3, 4].map((tick) => {
                        const y = top + height * tick / 4;
                        return (
                            <g key={tick}>
                                <line x1={left} y1={y} x2={left + width} y2={y} stroke="#e5e7eb" strokeDasharray="3 3" />
                                <text x={left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">{formatValue(ceiling * (1 - tick / 4))}</text>
                            </g>
                        );
                    })}
                    {points.map((point, index) => {
                        const value = Number(point[metric]) || 0;
                        const barHeight = value / ceiling * height;
                        const description = `${point.date} (UTC): ${formatValue(value)}`;
                        return (
                            <g key={point.date}>
                                <rect x={left + index * step + step * 0.15} y={top + height - barHeight} width={step * 0.7} height={barHeight} rx="3" fill="#6366f1" />
                                <rect x={left + index * step} y={top} width={step} height={height} fill="transparent" tabIndex="0" role="img" aria-label={description}>
                                    <title>{description}</title>
                                </rect>
                                {(index % labelStep === 0 || index === points.length - 1) && (
                                    <text x={left + (index + 0.5) * step} y={top + height + 24} textAnchor="middle" fontSize="11" fill="#6b7280">{dayjs(point.date).format('MMM D')}</text>
                                )}
                            </g>
                        );
                    })}
                    {max === 0 && <text x={left + width / 2} y={top + height / 2} textAnchor="middle" fontSize="13" fill="#6b7280">{hasLogs && data.length === 0 ? 'Usage dates unavailable for these logs' : hasLogs ? `No ${metric} recorded in this date range` : 'No usage in this date range'}</text>}
                </svg>
            </div>
        </section>
    );
};

export default AIUsageChart;
